import type { RuntimeConfig } from '../config';

const identityKey = 'scrapline:identity-token';
const requestTimeoutMs = 8_000;
const sessionReplacementGraceMs = 500;
const resultRefreshDelaysMs = [150, 250, 500, 1_000, 2_000, 3_000, 5_000] as const;

export interface PlayerProfile {
  recordedReceiptId?: string;
  id: string;
  displayName: string;
  callTag?: string;
  email?: string;
  guest: boolean;
  bankedValue: number;
  lifetimeScore: number;
  matchesPlayed: number;
  extractions: number;
  falls: number;
  eliminations?: number;
  cosmetic?: string;
  callTagChangeAvailableAt?: string;
  publicIdentityRestricted?: boolean;
  createdAt: string;
}

export interface CosmeticOption {
  id: string;
  label: string;
  description: string;
  bankRequired: number;
  unlocked: boolean;
}

export interface CosmeticLoadout {
  selected: string;
  options: CosmeticOption[];
}

export interface MatchHistoryEntry {
  receiptId?: string;
  outcome: 'extracted' | 'lost';
  bankedValue: number;
  lostValue: number;
  score: number;
  extractionStop: number;
  multiplier: number;
  falls: number;
  eliminations: number;
  completedAt: string;
}

interface IdentitySession {
  token: string;
  expiresAt: string;
  profile: PlayerProfile;
}

export type SessionRestoration = 'none' | 'checking' | 'unavailable';

export interface ResultRefresh {
  state: 'recorded' | 'delayed' | 'unavailable' | 'session-changed';
  profile: PlayerProfile | null;
}

export class IdentityError extends Error {
  public constructor(message: string, public readonly code: string) {
    super(message);
  }
}

export class IdentityService {
  private token = localStorage.getItem(identityKey) ?? '';
  private profile: PlayerProfile | null = null;
  private restorePromise: Promise<void>;
  private createPromise: Promise<PlayerProfile> | null = null;
  private resultRefreshPromise: Promise<ResultRefresh> | null = null;
  private resultRefreshRevision = -1;
  private resultRefreshReceipt: string | undefined;
  private readonly listeners = new Set<(profile: PlayerProfile | null) => void>();
  private sessionRevision = 0;
  private restorationState: SessionRestoration = 'none';

  public constructor(private readonly config: RuntimeConfig) {
    window.addEventListener('storage', this.handleStoredSession);
    this.restorePromise = this.restore();
  }

  public current(): PlayerProfile | null {
    return this.profile;
  }

  public restoration(): SessionRestoration {
    return this.restorationState;
  }

  public async retryRestoration(): Promise<void> {
    if (this.restorationState === 'unavailable' && this.token && !this.profile) {
      this.restorePromise = this.restore();
    }
    await this.restorePromise;
  }

  public authorization(): string | null {
    return this.token ? `Bearer ${this.token}` : null;
  }

  public expireAuthorization(authorization: string): void {
    if (this.authorization() !== authorization) return;
    // A storage replacement is propagated asynchronously between Chromium
    // renderer processes. A gameplay socket can reject its old authorization
    // inside the narrow gap after another tab rotated the password but before
    // this tab sees the new token. Give that queued handoff one bounded turn,
    // then adopt and validate it instead of deleting it from every tab.
    window.setTimeout(() => {
      if (this.authorization() !== authorization) return;
      const storedToken = localStorage.getItem(identityKey) ?? '';
      if (storedToken && storedToken !== this.token) {
        this.adoptStoredSession(storedToken);
        return;
      }
      this.clear();
    }, sessionReplacementGraceMs);
  }

  public subscribe(listener: (profile: PlayerProfile | null) => void): () => void {
    this.listeners.add(listener);
    listener(this.profile);
    return () => this.listeners.delete(listener);
  }

  public async ready(): Promise<PlayerProfile | null> {
    await this.restorePromise;
    return this.profile;
  }

  public async ensureGuest(displayName: string): Promise<PlayerProfile> {
    await this.restorePromise;
    if (this.token && !this.profile) {
      const sourceToken = this.token;
      const sourceRevision = this.sessionRevision;
      try {
        const profile = await this.request<PlayerProfile>('/api/v1/me', {}, true, sourceToken);
        this.requireCurrentSession(sourceToken, sourceRevision);
        this.acceptProfile(profile);
      } catch (error) {
        if (error instanceof IdentityError && error.code === 'session_expired' && this.isCurrentSession(sourceToken, sourceRevision)) this.clear();
        else throw error;
      }
    }
    if (this.profile && this.token) return this.profile;
    if (!this.createPromise) {
      this.createPromise = this.request<IdentitySession>('/api/v1/auth/guest', {
        method: 'POST',
        body: JSON.stringify({ displayName }),
      }).then((session) => {
        this.accept(session);
        return session.profile;
      }).finally(() => {
        this.createPromise = null;
      });
    }
    return this.createPromise;
  }

  public async refresh(receiptId?: string): Promise<PlayerProfile | null> {
    await this.restorePromise;
    const sourceToken = this.token;
    const sourceRevision = this.sessionRevision;
    if (!sourceToken) return null;
    try {
      const path = '/api/v1/me'+(receiptId ? `?receipt=${encodeURIComponent(receiptId)}` : '');
      const profile = await this.request<PlayerProfile>(path, {}, true, sourceToken);
      if (!this.isCurrentSession(sourceToken, sourceRevision)) return this.profile;
      this.acceptProfile(profile);
      return this.profile;
    } catch (error) {
      if (!this.isCurrentSession(sourceToken, sourceRevision)) return this.profile;
      if (error instanceof IdentityError && error.code === 'session_expired') this.clear();
      throw error;
    }
  }

  public async refreshAfterResult(receiptId?: string): Promise<ResultRefresh> {
    await this.restorePromise;
    if (!this.token) return { state: 'session-changed', profile: null };
    if (this.resultRefreshPromise && this.resultRefreshRevision === this.sessionRevision
      && this.resultRefreshReceipt === receiptId) return this.resultRefreshPromise;
    const baselineMatches = this.profile?.matchesPlayed ?? 0;
    this.resultRefreshRevision = this.sessionRevision;
    this.resultRefreshReceipt = receiptId;
    const refresh = this.pollForRecordedResult(baselineMatches, this.token, this.sessionRevision, receiptId);
    const shared = refresh.finally(() => {
      if (this.resultRefreshPromise === shared) this.resultRefreshPromise = null;
    });
    this.resultRefreshPromise = shared;
    return shared;
  }

  public async register(callTag: string, email: string, password: string): Promise<PlayerProfile> {
    await this.restorePromise;
    const session = await this.request<IdentitySession>('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ callTag, email, password }),
    }, Boolean(this.token));
    this.accept(session);
    return session.profile;
  }

  public async login(identifier: string, password: string): Promise<PlayerProfile> {
    const session = await this.request<IdentitySession>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ callTag: identifier, password }),
    });
    this.accept(session);
    return session.profile;
  }

  public async logout(): Promise<void> {
    await this.restorePromise;
    const sourceToken = this.token;
    const sourceRevision = this.sessionRevision;
    if (sourceToken) {
      try {
        await this.request<void>('/api/v1/auth/logout', { method: 'POST' }, true, sourceToken);
      } catch {
        // Local logout remains available during an outage.
      }
    }
    // A second tab may have replaced the browser-wide credential while this
    // request was in flight. Never erase or orphan that newer session because
    // an older logout response arrived late.
    if (!sourceToken || this.isCurrentSession(sourceToken, sourceRevision)) this.clear();
  }

  public async cosmetics(): Promise<CosmeticLoadout> {
    await this.restorePromise;
    const sourceToken = this.token;
    const sourceRevision = this.sessionRevision;
    const loadout = await this.request<CosmeticLoadout>('/api/v1/me/cosmetics', {}, true, sourceToken);
    this.requireCurrentSession(sourceToken, sourceRevision);
    return loadout;
  }

  public async matchHistory(): Promise<MatchHistoryEntry[]> {
    await this.restorePromise;
    const sourceToken = this.token;
    const sourceRevision = this.sessionRevision;
    const history = await this.request<MatchHistoryEntry[]>('/api/v1/me/matches', {}, true, sourceToken);
    this.requireCurrentSession(sourceToken, sourceRevision);
    return history;
  }

  public async equipCosmetic(cosmetic: string): Promise<PlayerProfile> {
    await this.restorePromise;
    const sourceToken = this.token;
    const sourceRevision = this.sessionRevision;
    const profile = await this.request<PlayerProfile>('/api/v1/me/cosmetics', {
      method: 'PUT',
      body: JSON.stringify({ cosmetic }),
    }, true, sourceToken);
    this.requireCurrentSession(sourceToken, sourceRevision);
    this.acceptProfile(profile);
    return profile;
  }

  public async changeCallTag(callTag: string, password: string): Promise<PlayerProfile> {
    await this.restorePromise;
    const sourceToken = this.token;
    const sourceRevision = this.sessionRevision;
    const profile = await this.request<PlayerProfile>('/api/v1/me/call-tag', {
      method: 'PUT',
      body: JSON.stringify({ callTag, password }),
    }, true, sourceToken);
    this.requireCurrentSession(sourceToken, sourceRevision);
    this.acceptProfile(profile);
    return profile;
  }

  public async changePassword(currentPassword: string, newPassword: string): Promise<PlayerProfile> {
    await this.restorePromise;
    const sourceToken = this.token;
    const sourceRevision = this.sessionRevision;
    const session = await this.request<IdentitySession>('/api/v1/me/password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    }, true, sourceToken);
    this.requireCurrentSession(sourceToken, sourceRevision);
    this.accept(session);
    return session.profile;
  }

  private async restore(): Promise<void> {
    const sourceToken = this.token;
    const sourceRevision = this.sessionRevision;
    if (!sourceToken) return;
    this.restorationState = 'checking';
    this.emit();
    try {
      const profile = await this.request<PlayerProfile>('/api/v1/me', {}, true, sourceToken);
      if (this.token !== sourceToken || this.sessionRevision !== sourceRevision) return;
      this.acceptProfile(profile);
    } catch (error) {
      if (!this.isCurrentSession(sourceToken, sourceRevision)) return;
      if (error instanceof IdentityError && error.code === 'session_expired') {
        this.clear();
        return;
      }
      this.restorationState = 'unavailable';
      this.emit();
    }
  }

  private accept(session: IdentitySession): void {
    this.sessionRevision += 1;
    this.token = session.token;
    localStorage.setItem(identityKey, session.token);
    this.acceptProfile(session.profile);
  }

  private acceptProfile(profile: PlayerProfile): void {
    this.profile = profile;
    this.restorationState = 'none';
    this.emit();
  }

  private clear(): void {
    this.sessionRevision += 1;
    this.token = '';
    this.profile = null;
    this.restorationState = 'none';
    localStorage.removeItem(identityKey);
    this.emit();
  }

  private readonly handleStoredSession = (event: StorageEvent): void => {
    if ((event.key !== identityKey && event.key !== null) || (event.storageArea && event.storageArea !== localStorage)) return;
    const token = event.key === null ? localStorage.getItem(identityKey) ?? '' : event.newValue ?? '';
    if (token === this.token) return;
    if (!token) {
      this.sessionRevision += 1;
      this.token = '';
      this.profile = null;
      this.restorationState = 'none';
      this.emit();
      return;
    }
    this.adoptStoredSession(token);
  };

  private adoptStoredSession(token: string): void {
    this.sessionRevision += 1;
    this.token = token;
    // A bearer is not an identity until the server validates it. Clear the
    // former account immediately so its private Station state cannot remain
    // visible while another tab's replacement token is being checked.
    this.profile = null;
    this.restorePromise = this.restore();
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.profile);
  }

  private isCurrentSession(token: string, revision: number): boolean {
    return Boolean(token) && this.token === token && this.sessionRevision === revision;
  }

  private requireCurrentSession(token: string, revision: number): void {
    if (!this.isCurrentSession(token, revision)) {
      throw new IdentityError('The account session changed. Log in again.', 'session_changed');
    }
  }

  // Match results are accepted synchronously by the authoritative simulation
  // but written by a bounded persistence worker. A single fixed-delay read can
  // therefore win the race and leave the Station cached at its pre-run bank.
  // Confirm the exact public receipt with bounded backoff; older API matches
  // without receipts retain the monotonic-counter fallback. Every successful
  // read still emits immediately, so a player who returns early sees the total
  // correct itself without reopening the account panel or refreshing the page.
  private async pollForRecordedResult(baselineMatches: number, token: string, revision: number, receiptId?: string): Promise<ResultRefresh> {
    let latest = this.profile;
    let state: ResultRefresh['state'] = 'unavailable';
    for (const delay of resultRefreshDelaysMs) {
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      if (!this.isCurrentSession(token, revision)) return { state: 'session-changed', profile: null };
      try {
        latest = await this.refresh(receiptId);
        if (!this.isCurrentSession(token, revision)) return { state: 'session-changed', profile: null };
        const recorded = receiptId ? latest?.recordedReceiptId === receiptId : (latest?.matchesPlayed ?? 0) > baselineMatches;
        if (recorded) return { state: 'recorded', profile: latest };
        state = 'delayed';
      } catch {
        if (!this.isCurrentSession(token, revision)) return { state: 'session-changed', profile: null };
        state = 'unavailable';
      }
    }
    return { state, profile: latest };
  }

  private async request<T>(path: string, init: RequestInit = {}, authenticated = false, token = this.token): Promise<T> {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), requestTimeoutMs);
    try {
      const authorization = authenticated && token ? `Bearer ${token}` : null;
      const response = await fetch(this.config.apiOrigin + path, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          ...(authorization ? { Authorization: authorization } : {}),
          ...init.headers,
        },
        signal: controller.signal,
      });
      if (response.status === 204) return undefined as T;
      const payload = await response.json() as T & { error?: { code?: string; message?: string } };
      if (!response.ok) {
        throw new IdentityError(payload.error?.message ?? 'The yard signal failed.', payload.error?.code ?? 'request_failed');
      }
      return payload;
    } catch (error) {
      if (error instanceof IdentityError) throw error;
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new IdentityError('The yard did not answer in time.', 'timeout');
      }
      throw new IdentityError('Cannot reach the SCRAPLINE server.', 'offline');
    } finally {
      window.clearTimeout(timer);
    }
  }
}
